/**
 * 🎫 SorteoList.jsx
 * Búsqueda y validación de códigos de sorteo (móvil + feria).
 * La validación de reglas de negocio ocurre solo en el backend.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Search, Ticket, CheckCircle2 } from "lucide-react";
import Swal from "sweetalert2";
import {
    buscarRegistrosSorteo,
    obtenerResumenSorteo,
    validarRegistroSorteo,
} from "../../services/SorteoService";

const ORIGENES = ["TODOS", "MOVIL", "FERIA"];
const ESTADOS = ["TODOS", "PENDIENTE", "VALIDADO"];
const PROMOCIONES = [
    { value: "TODOS", label: "Todos" },
    { value: "SI", label: "Sí aceptó" },
    { value: "NO", label: "No aceptó" },
    { value: "SIN_INFORMACION", label: "Sin información" },
];

const RESUMEN_VACIO = {
    total: 0,
    totalFeria: 0,
    pendientes: 0,
    validados: 0,
    promocionesAceptadas: 0,
    promocionesNoAceptadas: 0,
    promocionesSinInformacion: 0,
};

const ORIGEN_LABEL = {
    MOVIL: "Móvil",
    FERIA: "Feria",
};

const ESTADO_UI = {
    PENDIENTE: "bg-amber-100 text-amber-800",
    VALIDADO: "bg-green-100 text-green-700",
    EXPIRADO: "bg-gray-100 text-gray-600",
    ANULADO: "bg-red-100 text-red-700",
};

const ERROR_MESSAGES = {
    SORTEO_CODIGO_YA_VALIDADO: "Este código de sorteo ya fue validado.",
    SORTEO_CODIGO_EXPIRADO: "No se pudo validar este código.",
    SORTEO_CODIGO_NO_ENCONTRADO: "No se encontró el código de sorteo.",
};

function formatDateTime(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("es-BO", {
        dateStyle: "short",
        timeStyle: "short",
    });
}

function mensajeErrorValidacion(error) {
    const data = error?.response?.data || {};
    const code = data.error || data.code;
    if (code && ERROR_MESSAGES[code]) {
        return ERROR_MESSAGES[code];
    }
    if (typeof data.message === "string" && data.message.trim()) {
        return data.message;
    }
    return "No se pudo validar el código.";
}

function rowKey(row) {
    return `${row.origen}-${row.id}`;
}

/** FERIA: true/false/null son distintos. MOVIL no tiene esta autorización. */
function etiquetaPromociones(row) {
    if (row.origen !== "FERIA") return "Promociones: No aplica";
    if (row.aceptaComunicacionesComerciales === true) return "Promociones: Sí";
    if (row.aceptaComunicacionesComerciales === false) return "Promociones: No";
    return "Promociones: Sin información";
}

export default function SorteoList() {
    const [q, setQ] = useState("");
    const [origen, setOrigen] = useState("TODOS");
    const [estado, setEstado] = useState("TODOS");
    const [promociones, setPromociones] = useState("TODOS");
    const [registros, setRegistros] = useState([]);
    const [resumen, setResumen] = useState(RESUMEN_VACIO);
    const [loading, setLoading] = useState(true);
    const [procesandoId, setProcesandoId] = useState(null);

    const fetchRegistros = useCallback(async () => {
        try {
            setLoading(true);
            const response = await buscarRegistrosSorteo({
                q,
                origen,
                estado,
                promociones,
            });
            const payload = response.data;
            const list = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.data)
                    ? payload.data
                    : [];
            setRegistros(list);
        } catch (error) {
            console.error("Error al cargar sorteo:", error);
            Swal.fire(
                "Error",
                error?.response?.data?.message || "No se pudieron cargar los registros",
                "error"
            );
            setRegistros([]);
        } finally {
            setLoading(false);
        }
    }, [q, origen, estado, promociones]);

    const fetchResumen = useCallback(async () => {
        try {
            const response = await obtenerResumenSorteo();
            setResumen({ ...RESUMEN_VACIO, ...(response.data || {}) });
        } catch (error) {
            console.error("Error al cargar resumen de sorteo:", error);
        }
    }, []);

    const refrescarListaYResumen = useCallback(async () => {
        await Promise.all([fetchRegistros(), fetchResumen()]);
    }, [fetchRegistros, fetchResumen]);

    useEffect(() => {
        fetchRegistros();
    }, [fetchRegistros]);

    useEffect(() => {
        fetchResumen();
    }, [fetchResumen]);

    const handleBuscar = (event) => {
        event.preventDefault();
        fetchRegistros();
    };

    const handleValidar = async (row) => {
        if (row.estado !== "PENDIENTE" || procesandoId) return;

        const result = await Swal.fire({
            title: "¿Validar código?",
            html: `<p><strong>${row.codigoSorteo}</strong> — ${row.nombre || "Sin nombre"}</p>
                   <p class="text-sm text-gray-500">Origen: ${ORIGEN_LABEL[row.origen] || row.origen}</p>`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Validar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#0E1B31",
        });

        if (!result.isConfirmed) return;

        const key = rowKey(row);
        setProcesandoId(key);

        try {
            await validarRegistroSorteo({
                origen: row.origen,
                sourceId: row.id,
            });
            await Swal.fire({
                icon: "success",
                title: "Código validado",
                text: `El código ${row.codigoSorteo} quedó validado.`,
                confirmButtonColor: "#0E1B31",
            });
            await refrescarListaYResumen();
        } catch (error) {
            const status = error?.response?.status;
            const icon = status === 404 ? "warning" : "error";
            Swal.fire({
                icon,
                title: status === 409 ? "No se pudo validar" : "Error",
                text: mensajeErrorValidacion(error),
                confirmButtonColor: "#0E1B31",
            });
            if (status === 409 || status === 404) {
                await refrescarListaYResumen();
            }
        } finally {
            setProcesandoId(null);
        }
    };

    const tarjetas = [
        { label: "Total registrados en web", value: resumen.totalFeria },
        { label: "Pendientes", value: resumen.pendientes },
        { label: "Validados", value: resumen.validados },
        { label: "Aceptaron promociones", value: resumen.promocionesAceptadas },
        { label: "No aceptaron promociones", value: resumen.promocionesNoAceptadas },
        { label: "Sin información", value: resumen.promocionesSinInformacion },
    ];

    return (
        <div className="space-y-6">
            <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                    {tarjetas.map((card) => (
                        <div
                            key={card.label}
                            className="bg-white rounded-xl p-4 shadow-md min-w-0"
                        >
                            <p className="text-xs sm:text-sm text-gray-500 font-medium leading-snug">
                                {card.label}
                            </p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                                {card.value}
                            </p>
                        </div>
                    ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                    Sin información corresponde a registros anteriores a la captura
                    de esta autorización.
                </p>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-200 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-herbalife-lighter rounded-lg">
                            <Ticket className="w-6 h-6 text-herbalife-green" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800">Sorteo / Feria</h2>
                            <p className="text-sm text-gray-500">
                                Busca códigos de la app móvil y de la landing de feria.
                                Los códigos de feria permanecen pendientes hasta validarlos.
                            </p>
                        </div>
                    </div>

                    <form
                        onSubmit={handleBuscar}
                        className="flex flex-col lg:flex-row gap-3 lg:items-end"
                    >
                        <label className="flex-1 text-sm font-medium text-gray-700">
                            Buscar
                            <div className="relative mt-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Código, nombre, correo o teléfono"
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-herbalife-light outline-none"
                                />
                            </div>
                        </label>

                        <label className="text-sm font-medium text-gray-700">
                            Origen
                            <select
                                value={origen}
                                onChange={(e) => setOrigen(e.target.value)}
                                className="mt-1 block w-full lg:w-40 px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-herbalife-light"
                            >
                                {ORIGENES.map((value) => (
                                    <option key={value} value={value}>
                                        {value === "TODOS"
                                            ? "Todos"
                                            : ORIGEN_LABEL[value] || value}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="text-sm font-medium text-gray-700">
                            Estado
                            <select
                                value={estado}
                                onChange={(e) => setEstado(e.target.value)}
                                className="mt-1 block w-full lg:w-44 px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-herbalife-light"
                            >
                                {ESTADOS.map((value) => (
                                    <option key={value} value={value}>
                                        {value === "TODOS"
                                            ? "Todos"
                                            : value.charAt(0) + value.slice(1).toLowerCase()}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="text-sm font-medium text-gray-700">
                            Promociones
                            <select
                                value={promociones}
                                onChange={(e) => setPromociones(e.target.value)}
                                className="mt-1 block w-full lg:w-48 px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-herbalife-light"
                            >
                                {PROMOCIONES.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <button
                            type="submit"
                            className="px-5 py-2 bg-herbalife-green text-white rounded-lg hover:bg-herbalife-dark font-medium"
                        >
                            Buscar
                        </button>
                    </form>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin h-8 w-8 border-4 border-herbalife-green border-t-transparent rounded-full" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                        Código
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                        Origen
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                        Nombre
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                        Contacto
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                        Promociones
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                        Estado
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                        Validado en
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                                        Acción
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {registros.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            className="px-4 py-10 text-center text-gray-500"
                                        >
                                            No hay registros para los filtros actuales.
                                        </td>
                                    </tr>
                                ) : (
                                    registros.map((row) => {
                                        const key = rowKey(row);
                                        const puedeValidar = row.estado === "PENDIENTE";
                                        const ocupado = procesandoId === key;

                                        return (
                                            <tr key={key} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 font-mono text-lg font-bold text-gray-900">
                                                    {row.codigoSorteo}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {ORIGEN_LABEL[row.origen] || row.origen}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-800">
                                                    {row.nombre || "—"}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    <div>{row.email || "—"}</div>
                                                    <div>{row.telefono || row.whatsapp || ""}</div>
                                                    {row.pais || row.departamento || row.ciudad ? (
                                                        <div className="mt-1 text-xs text-gray-500">
                                                            {[row.pais, row.departamento, row.ciudad]
                                                                .filter(Boolean)
                                                                .join(" · ")}
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                    {etiquetaPromociones(row)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                                                            ESTADO_UI[row.estado] ||
                                                            "bg-gray-100 text-gray-700"
                                                        }`}
                                                    >
                                                        {row.estado}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                    {formatDateTime(row.validatedAt)}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    {puedeValidar ? (
                                                        <button
                                                            type="button"
                                                            disabled={Boolean(procesandoId)}
                                                            onClick={() => handleValidar(row)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-herbalife-green text-white hover:bg-herbalife-dark disabled:opacity-50"
                                                        >
                                                            <CheckCircle2 className="w-4 h-4" />
                                                            {ocupado ? "Validando…" : "Validar"}
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">
                                                            —
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
